import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  View,
  TouchableOpacity,
  Image,
  ActivityIndicator,
  ScrollView,
  StyleSheet,
} from "react-native";
import Text from "@components/CustomText";
import BottomSheet from "@components/ds/BottomSheet";
import SheetButtons from "@components/ds/SheetButtons";
import useUploadImageFromPicker from "../hooks/useUploadImageFromPicker";
import { tokens } from "../styles";

// Marking sheet — mirrors crate-cx-items.vue's openMarkingModal.
// Per-reason +/- steppers, hidden/shown by is_cdc_order / is_top_sku flags.
// Photos are optional across all reasons — backend kernel constant
// images_mandatory_for_samasya is now all-false so the executive can submit
// damaged / wrong-item markings without evidence, matching the web flow.

const REASONS = [
  {
    key: "item_damaged",
    label: "Damaged",
    alwaysShown: true,
    requiresImage: false,
  },
  {
    key: "wrong_item_received",
    label: "Wrong Item",
    hideForCdc: true,
    hideForTopSku: true,
    requiresImage: false,
  },
  { key: "not_found", label: "Missing", hideForCdc: true },
];

const MAX_IMAGES_PER_REASON = 3;

const buildReasons = (item) => {
  const isCdc = Boolean(item?.is_cdc_order);
  const isTopSku = Boolean(item?.is_top_sku);
  return REASONS.filter((r) => {
    if (r.alwaysShown) return true;
    if (r.hideForCdc && isCdc) return false;
    if (r.hideForTopSku && isTopSku) return false;
    return true;
  });
};

const MarkingSheet = ({ visible, item, onClose, onSubmit, submitting }) => {
  const reasons = useMemo(() => buildReasons(item), [item]);
  const [diff, setDiff] = useState({});
  const [images, setImages] = useState({});
  const { pick, uploading } = useUploadImageFromPicker();

  useEffect(() => {
    if (!visible) return;
    const initDiff = {};
    const initImages = {};
    reasons.forEach((r) => {
      initDiff[r.key] = 0;
      initImages[r.key] = [];
    });
    setDiff(initDiff);
    setImages(initImages);
  }, [visible, reasons]);

  const total = Object.values(diff).reduce((sum, n) => sum + Number(n || 0), 0);
  const available = Number(item?.quantity || 0);
  const exceeds = total > available;

  const bump = (key, delta) => {
    setDiff((prev) => {
      const current = Number(prev[key] || 0);
      const candidate = current + delta;
      if (candidate < 0) return prev;
      if (delta > 0 && total + delta > available) return prev;
      return { ...prev, [key]: candidate };
    });
  };

  // Gate against double-tap: `uploading` only flips true *after* the user
  // selects an asset, so a frantic double-tap before the picker mounts can
  // fire two pick() Promises and stack two Camera/Gallery alerts. This
  // synchronous ref blocks the second tap inside the same render frame.
  const pickingNow = useRef(false);
  const isMountedRef = useRef(true);
  useEffect(
    () => () => {
      isMountedRef.current = false;
    },
    [],
  );

  const addImage = async (key) => {
    const list = images[key] || [];
    if (list.length >= MAX_IMAGES_PER_REASON) return;
    if (pickingNow.current) return;
    pickingNow.current = true;
    try {
      const url = await pick();
      if (!url) return;
      if (!isMountedRef.current) return;
      setImages((prev) => ({ ...prev, [key]: [...(prev[key] || []), url] }));
    } finally {
      pickingNow.current = false;
    }
  };

  const removeImage = (key, idx) => {
    setImages((prev) => ({
      ...prev,
      [key]: (prev[key] || []).filter((_, i) => i !== idx),
    }));
  };

  // Each reason that requires images and has qty > 0 must have ≥ 1 image.
  const missingImageReasons = reasons.filter((r) => {
    if (!r.requiresImage) return false;
    const qty = Number(diff[r.key] || 0);
    if (qty === 0) return false;
    return (images[r.key] || []).length === 0;
  });
  const blocked = total === 0 || exceeds || missingImageReasons.length > 0;

  const handleConfirm = () => {
    if (!item || blocked) return;
    onSubmit({ item, diff, images });
  };

  return (
    <BottomSheet
      visible={visible}
      title="Mark Item Issue"
      subtitle={item?.short_name || ""}
      onClose={onClose}
      footer={
        <SheetButtons
          onCancel={onClose}
          onConfirm={handleConfirm}
          loading={submitting || uploading}
          disabled={blocked}
          confirmLabel="Submit"
        />
      }
    >
      <ScrollView keyboardShouldPersistTaps="handled" style={styles.scroll}>
        {item ? (
          <Text variant="caption_2" style={styles.summary}>
            Customer: {item.user_name || "—"} · Qty: {available}
          </Text>
        ) : null}
        {reasons.map((r) => {
          const value = Number(diff[r.key] || 0);
          const reasonImages = images[r.key] || [];
          const showImages = r.requiresImage && value > 0;
          return (
            <View key={r.key} style={styles.reasonBlock}>
              <View style={styles.reasonRow}>
                <Text variant="caption_2" style={styles.reasonLabel}>
                  {r.label}
                  {r.requiresImage ? " *" : ""}
                </Text>
                <View style={styles.stepper}>
                  <TouchableOpacity
                    activeOpacity={0.85}
                    onPress={() => bump(r.key, -1)}
                    disabled={value === 0}
                    style={[
                      styles.stepBtn,
                      value === 0 && styles.stepBtnDisabled,
                    ]}
                  >
                    <Text variant="caption_2" style={styles.stepText}>
                      −
                    </Text>
                  </TouchableOpacity>
                  <Text variant="caption_2" style={styles.stepValue}>
                    {value}
                  </Text>
                  <TouchableOpacity
                    activeOpacity={0.85}
                    onPress={() => bump(r.key, +1)}
                    disabled={total >= available}
                    style={[
                      styles.stepBtn,
                      total >= available && styles.stepBtnDisabled,
                    ]}
                  >
                    <Text variant="caption_2" style={styles.stepText}>
                      +
                    </Text>
                  </TouchableOpacity>
                </View>
              </View>
              {showImages ? (
                <View style={styles.imagesWrap}>
                  <View style={styles.thumbsRow}>
                    {reasonImages.map((url, idx) => (
                      <View key={`${url}-${idx}`} style={styles.thumb}>
                        <Image source={{ uri: url }} style={styles.thumbImg} />
                        <TouchableOpacity
                          activeOpacity={0.7}
                          onPress={() => removeImage(r.key, idx)}
                          hitSlop={{ top: 6, bottom: 6, left: 6, right: 6 }}
                          style={styles.thumbClose}
                        >
                          <Text
                            variant="caption_2"
                            style={styles.thumbCloseText}
                          >
                            ×
                          </Text>
                        </TouchableOpacity>
                      </View>
                    ))}
                    {reasonImages.length < MAX_IMAGES_PER_REASON ? (
                      <TouchableOpacity
                        activeOpacity={0.85}
                        onPress={() => addImage(r.key)}
                        disabled={uploading}
                        style={[
                          styles.addThumb,
                          uploading && styles.addThumbBusy,
                        ]}
                      >
                        {uploading ? (
                          <ActivityIndicator color={tokens.textSecondary} />
                        ) : (
                          <Text variant="caption_2" style={styles.addThumbText}>
                            + Photo
                          </Text>
                        )}
                      </TouchableOpacity>
                    ) : null}
                  </View>
                  {reasonImages.length === 0 ? (
                    <Text variant="caption_2" style={styles.evidence}>
                      Add at least one photo as evidence.
                    </Text>
                  ) : null}
                </View>
              ) : null}
            </View>
          );
        })}
        <Text variant="caption_2" style={styles.totals}>
          Marked: {total} / {available}
        </Text>
        {exceeds ? (
          <Text variant="caption_2" style={styles.error}>
            Cannot exceed available quantity.
          </Text>
        ) : null}
        {missingImageReasons.length > 0 ? (
          <Text variant="caption_2" style={styles.error}>
            {missingImageReasons.map((r) => r.label).join(", ")} requires at
            least one photo.
          </Text>
        ) : null}
      </ScrollView>
    </BottomSheet>
  );
};

export default MarkingSheet;

const styles = StyleSheet.create({
  scroll: { maxHeight: 460 },
  summary: {
    color: tokens.textSecondary,
    fontSize: 12,
    marginBottom: tokens.spacing.medium,
  },
  reasonBlock: {
    paddingVertical: tokens.spacing.small,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: tokens.borderLight,
  },
  reasonRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  reasonLabel: { color: tokens.textPrimary, fontSize: 13, flexShrink: 1 },
  stepper: { flexDirection: "row", alignItems: "center" },
  stepBtn: {
    height: 30,
    width: 30,
    borderRadius: 15,
    borderWidth: 1,
    borderColor: tokens.borderMedium,
    alignItems: "center",
    justifyContent: "center",
  },
  stepBtnDisabled: { opacity: 0.4 },
  stepText: { color: tokens.textPrimary, fontSize: 16, fontWeight: "700" },
  stepValue: {
    color: tokens.textPrimary,
    fontSize: 14,
    minWidth: 32,
    textAlign: "center",
    fontWeight: "700",
  },
  imagesWrap: { marginTop: tokens.spacing.small },
  thumbsRow: { flexDirection: "row", flexWrap: "wrap" },
  thumb: {
    width: 64,
    height: 64,
    borderRadius: tokens.radius.sm,
    marginRight: 6,
    marginBottom: 6,
    backgroundColor: tokens.borderLight,
    overflow: "hidden",
  },
  thumbImg: { width: 64, height: 64 },
  thumbClose: {
    position: "absolute",
    top: 2,
    right: 2,
    width: 20,
    height: 20,
    borderRadius: 10,
    backgroundColor: tokens.error,
    alignItems: "center",
    justifyContent: "center",
  },
  thumbCloseText: {
    color: tokens.surface,
    fontSize: 14,
    fontWeight: "700",
    lineHeight: 16,
  },
  addThumb: {
    width: 64,
    height: 64,
    borderRadius: tokens.radius.sm,
    borderWidth: 1,
    borderStyle: "dashed",
    borderColor: tokens.borderMedium,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: tokens.surface,
  },
  addThumbBusy: { opacity: 0.6 },
  addThumbText: {
    color: tokens.textSecondary,
    fontSize: 11,
    fontWeight: "700",
  },
  evidence: { color: tokens.error, fontSize: 11, marginTop: 4 },
  totals: {
    color: tokens.textSecondary,
    fontSize: 12,
    marginTop: tokens.spacing.medium,
  },
  error: { color: tokens.error, fontSize: 12, marginTop: 4 },
});
